// src/slices/labelsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UILabel } from '../services/types';
import { labelsService } from '../services/labels.service';

interface LabelsState {
  /** Full dataset for selectors/KPIs (optional but handy) */
  data: UILabel[];
  loading: boolean;
  error?: string;

  /** Pagination state for the table */
  pageData: UILabel[]; // current page items
  page: number; // 1-based
  pageSize: number; // items per page
  total: number; // total items
  pageLoading: boolean; // loading just the current page
}

const initialState: LabelsState = {
  data: [],
  loading: false,
  pageData: [],
  page: 1,
  pageSize: 25,
  total: 0,
  pageLoading: false,
};

/** Load ALL labels (used by KPIs/other views that need full list) */
export const fetchLabels = createAsyncThunk<UILabel[]>('labels/fetchAll', async () => {
  // zero-arg service
  return await labelsService.list();
});

/** Page fetch: get ALL (zero-arg service) then slice locally */
export const fetchLabelsPage = createAsyncThunk<
  { items: UILabel[]; total: number; page: number; pageSize: number },
  { page?: number; pageSize?: number } | void
>('labels/fetchPage', async (args, { getState }) => {
  const state = getState() as { labels: LabelsState };
  const page = args?.page ?? state.labels.page ?? 1;
  const pageSize = args?.pageSize ?? state.labels.pageSize ?? 25;
  const offset = (page - 1) * pageSize;

  // Prefer already-loaded data; otherwise fetch once
  const all = state.labels.data.length > 0 ? state.labels.data : await labelsService.list();

  const total = all.length;
  const items = all.slice(offset, offset + pageSize);

  return { items, total, page, pageSize };
});

const labelsSlice = createSlice({
  name: 'labels',
  initialState,
  reducers: {
    setLabelsPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLabelsPageSize(state, action: PayloadAction<number>) {
      state.pageSize = Math.max(1, action.payload);
      state.page = 1; // reset to first page when page size changes
    },
    resetLabelsPagination(state) {
      state.page = 1;
      state.pageSize = initialState.pageSize;
      state.total = 0;
      state.pageData = [];
      state.pageLoading = false;
    },
  },
  extraReducers: (b) => {
    // Full list channel
    b.addCase(fetchLabels.pending, (s) => {
      s.loading = true;
      s.error = undefined;
    })
      .addCase(fetchLabels.fulfilled, (s, a: PayloadAction<UILabel[]>) => {
        s.loading = false;
        s.data = a.payload;
        // Optional: keep pagination in sync if we're sitting on page 1
        if (s.page === 1 && s.total === 0) {
          s.total = a.payload.length;
          s.pageData = a.payload.slice(0, s.pageSize);
        }
      })
      .addCase(fetchLabels.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message;
      });

    // Paged channel
    b.addCase(fetchLabelsPage.pending, (s) => {
      s.pageLoading = true;
      s.error = undefined;
    })
      .addCase(
        fetchLabelsPage.fulfilled,
        (
          s,
          a: PayloadAction<{
            items: UILabel[];
            total: number;
            page: number;
            pageSize: number;
          }>,
        ) => {
          s.pageLoading = false;
          s.pageData = a.payload.items;
          s.total = a.payload.total;
          s.page = a.payload.page;
          s.pageSize = a.payload.pageSize;
          // Optional: seed full dataset if we didn't have it (keeps KPIs happy)
          if (s.data.length === 0 && a.payload.total > 0) {
            // We don't have the full array here, so leave s.data empty.
            // If you want to ensure s.data is filled, also dispatch fetchLabels() somewhere (e.g., on app startup).
          }
        },
      )
      .addCase(fetchLabelsPage.rejected, (s, a) => {
        s.pageLoading = false;
        s.error = a.error.message;
      });
  },
});

export const { setLabelsPage, setLabelsPageSize, resetLabelsPagination } = labelsSlice.actions;

export default labelsSlice.reducer;
